import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import swannLoading from "@packages/assets/swannloading.gif";

interface SimpleLoadingProps {
  showProgress?: boolean;
  fullHeight?: boolean;
  duration?: number; // Duration in seconds
  fastLoading?: boolean; // Enable fast moving bar animation
}

const SimpleLoading = ({
  showProgress = true,
  fullHeight = true,
  duration = 30, // Default 30 seconds
  fastLoading = false, // Default to false
  fullContainer = false,
}: SimpleLoadingProps) => {
  const [progress, setProgress] = useState(0);
  const [movingBarPosition, setMovingBarPosition] = useState(0);

  // Normal progress animation (when fastLoading is false)
  useEffect(() => {
    if (fastLoading) return; // Skip if fastLoading is true

    const startTime = Date.now();
    const endTime = startTime + duration * 1000;

    const interval = setInterval(() => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const totalDuration = duration * 1000;

      if (elapsed >= totalDuration) {
        setProgress(100);
        clearInterval(interval);
        return;
      }

      // Calculate progress with smooth easing
      const normalizedTime = elapsed / totalDuration;

      // Custom easing function for natural feel
      let easeProgress;
      if (normalizedTime < 0.2) {
        // Faster start (0-20%) - starts at 15% progress immediately
        easeProgress = 0.15 + normalizedTime * 0.25;
      } else if (normalizedTime < 0.6) {
        // Accelerated middle (20-60%)
        const t = (normalizedTime - 0.2) / 0.4;
        easeProgress = 0.4 + t * t * 0.3;
      } else {
        // Slow finish (60-100%)
        const t = (normalizedTime - 0.6) / 0.4;
        easeProgress = 0.7 + (1 - Math.pow(1 - t, 3)) * 0.3;
      }

      // Ensure progress never goes backwards and stays within bounds
      const finalProgress = Math.min(95, Math.max(0, easeProgress * 100));

      setProgress(finalProgress);
    }, 50); // Updates every 50ms for smooth animation

    return () => clearInterval(interval);
  }, [duration, fastLoading]);

  // Fast moving bar animation (when fastLoading is true)
  useEffect(() => {
    if (!fastLoading) return;

    const interval = setInterval(() => {
      setMovingBarPosition((prev) => {
        // Move from left to right, then reset
        if (prev >= 100) {
          return 0;
        }
        return prev + 2; // Speed of movement
      });
    }, 50); // Update every 50ms for smooth animation

    return () => clearInterval(interval);
  }, [fastLoading]);

  return (
    <div
      className={`${
        fullHeight ? "min-h-screen" : "min-h-[400px]"
      } bg-black flex flex-col items-center justify-center relative overflow-hidden ${fullContainer ? "mt-0" : "mt-6 rounded-tl-3xl"}`}
    >
      {/* Logo Section */}
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          {/* Logo */}
          <div className="w-28 h-28 flex items-center justify-center mb-0 mx-auto">
            <img src={swannLoading} alt="Loading" className="object-contain" />
          </div>

          {/* Progress Bar Section */}
          <div className="w-56 max-w-md px-8 pb-12">
            <div className="w-full h-1 bg-white/20 rounded-full overflow-hidden relative">
              {fastLoading ? (
                // Fast loading: Moving bar animation
                <div
                  className="absolute top-0 h-full w-6 bg-white rounded-full z-20 animate-pulse"
                  style={{
                    left: `${movingBarPosition}%`,
                    transform: "translateX(-50%)",
                    transition: "left 0.1s linear",
                    boxShadow: "0 0 8px rgba(255, 255, 255, 0.6)",
                  }}
                />
              ) : (
                // Normal loading: Progress bar with duration
                <div
                  className="h-full bg-white rounded-full transition-all duration-200 ease-out"
                  style={{ width: `${progress}%` }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleLoading;
